/**
 * HoSelectionsTab - Material selections tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 * Multi-quote comparison for each material.
 */

import type { VendorRatingSummary } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { SkeletonRows } from "@/components/ui/skeleton";
import { VendorRating } from "@/components/ui/vendor-rating";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import { useProjectShortlist } from "../../../hooks/use-projects";
import type { ShortlistQuote } from "../../../services/quotes-api.service";
import { selectQuote } from "../../../services/quotes-api.service";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	Icn,
	ItalicAccent,
	Pill,
	secondaryBtnStyle,
} from "../design-primitives";

/**
 * A quote price, in PAISE, as rupees.
 *
 * The `/ 100` is the bug this replaces, not a refinement. Every value this
 * function receives on this screen is a `shortlist_quotes.price` -- paise, per
 * the column's own documentation and `QuoteInput` -- and it was printed as
 * though it were rupees. A seeded tile quote of `48000` (Rs 480 per sq ft, a
 * realistic figure) rendered as "Rs 48,000 per sq ft", on the one screen whose
 * entire purpose is comparing prices before committing money.
 *
 * `features/realtor/utils/listings-format.ts` already has a paise-aware
 * formatter with a crore tier. It belongs in `shared-utils` so both can use it;
 * this keeps the local display style rather than changing how the screen looks
 * in a fix about arithmetic.
 */
const fmt = (paise: number) => {
	const rupees = Math.round(paise / 100);
	return rupees >= 100000
		? `₹${(rupees / 100000).toFixed(1)}L`
		: `₹${rupees.toLocaleString("en-IN")}`;
};

const ASSETS_URL = import.meta.env.VITE_ASSETS_URL as string;

interface MaterialItem {
	id: string;
	n: string;
	room: string;
	vendor: string;
	cost: number;
	spec: string;
	img: string;
	status: "approved" | "pending" | "designer";
	qty: string;
	shortlistEntryId?: string;
	quotes?: ShortlistQuote[];
	/**
	 * The APPROVED QUOTE'S ID, not its position.
	 *
	 * The previous shape was `selectedQuoteIndex`, and a position is not an
	 * identity: reordering the list moved the approval to a different vendor at a
	 * different price. Nothing in this component indexes into the array to decide
	 * what was approved.
	 */
	selectedQuoteId?: string | null;
}

const MAT_TONE = {
	approved: "done" as const,
	pending: "awaiting" as const,
	designer: "info" as const,
};
const MAT_LABEL = {
	approved: "You approved",
	pending: "Needs your approval",
	designer: "Designer proposing",
};

interface Quote {
	id: string;
	vendor: string;
	vendorCity: string | null;
	vendorIsVerified: boolean;
	vendorRating: VendorRatingSummary;
	price: number;
	unit: string;
	lead: string;
	tag: string | null;
	/** Who sourced it. Shown to the homeowner before they commit money. */
	addedByRole: "designer" | "contractor" | null;
}

function SelectionCompareCard({
	m,
	accent,
	onApprove,
	isApproving = false,
}: {
	m: MaterialItem;
	accent: string;
	onApprove: (
		name: string,
		choice: { name: string; price: number; vendor: string },
		// The quote's ID. Was `selectedIndex`, which is the whole defect: a
		// position identifies nothing once the list can change.
		quoteId: string,
	) => void;
	isApproving?: boolean;
}) {
	const hasQuotesFromApi = m.quotes && m.quotes.length > 0;

	// Keyed on the quote's ID. `?? first quote` only decides what is
	// PRE-HIGHLIGHTED before the homeowner has chosen; it is never what gets
	// approved, which is always an explicit id sent to the server.
	const [selId, setSelId] = React.useState<string | null>(
		m.selectedQuoteId ?? m.quotes?.[0]?.id ?? null,
	);
	// "Change choice" reopens an approved card for picking. Any quote may be
	// approved again, the current one included: re-approving it is how a
	// homeowner applies its price once a sent BOQ is being revised (#507).
	const [changing, setChanging] = React.useState(false);
	// Closed again when an approval request finishes, whether it succeeded or
	// failed; the tab's note says which.
	React.useEffect(() => {
		if (!isApproving) setChanging(false);
	}, [isApproving]);
	const approved = m.status === "approved" && !changing;

	if (!hasQuotesFromApi) {
		return (
			<FloatPanel elev={2} padding={24} style={{ textAlign: "center" }}>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 12,
						padding: "20px 0",
					}}
				>
					<Icn name="info" size={32} color="var(--color-dashboard-muted)" />
					{/*
					 * Name the product. The early return skips the layout that would
					 * normally render it, so without this the homeowner sees "no quotes
					 * added yet" attached to nothing identifiable -- which is barely an
					 * improvement on the entry being hidden altogether.
					 */}
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 17,
							color: "var(--color-dashboard-text)",
						}}
					>
						{m.n}
					</div>
					<div
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 16,
							color: "var(--color-dashboard-muted-dark)",
						}}
					>
						No quotes added yet
					</div>
					<div
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13,
							color: "var(--color-dashboard-muted)",
							maxWidth: 280,
						}}
					>
						Nobody has added vendor quotes for this product yet. You will be
						asked to choose once they have.
					</div>
				</div>
			</FloatPanel>
		);
	}

	const quotes: Quote[] = (m.quotes ?? []).map((q) => ({
		id: q.id,
		vendor: q.vendorName,
		vendorCity: q.vendorCity,
		vendorIsVerified: q.vendorIsVerified,
		vendorRating: q.vendorRating,
		price: q.price,
		unit: q.uom,
		lead:
			q.leadTimeDays !== null
				? `${q.leadTimeDays} day${q.leadTimeDays === 1 ? "" : "s"}`
				: (q.leadTimeNote ?? "Lead time not given"),
		tag: q.tag,
		addedByRole: q.addedByRole ?? null,
	}));

	const selected = quotes.find((q) => q.id === selId) ?? null;
	// What the homeowner actually approved, per the SERVER's record of it --
	// distinct from `selected`, which is the highlight before they commit.
	const approvedQuote = quotes.find((q) => q.id === m.selectedQuoteId) ?? null;
	// The cheapest quote is a stable reference point; `quotes[0]` was not, because
	// it moved whenever the list was reordered.
	const cheapest = quotes.reduce<Quote | null>(
		(best, q) => (best === null || q.price < best.price ? q : best),
		null,
	);

	return (
		<FloatPanel elev={2} padding={0} style={{ overflow: "hidden" }}>
			<div style={{ display: "flex", flexWrap: "wrap" }}>
				{/* Left: image */}
				<div
					style={{
						width: 240,
						flexShrink: 0,
						position: "relative",
						minHeight: 210,
					}}
				>
					<img
						src={m.img}
						alt=""
						style={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
							objectFit: "cover",
						}}
					/>
					<div
						style={{
							position: "absolute",
							inset: 0,
							background:
								"linear-gradient(180deg, color-mix(in srgb, var(--color-dashboard-scrim) 15%, transparent) 40%, color-mix(in srgb, var(--color-dashboard-scrim) 72%, transparent))",
						}}
					/>
					<Pill
						tone={MAT_TONE[m.status]}
						style={{
							position: "absolute",
							top: 12,
							left: 12,
							background:
								"color-mix(in srgb, var(--color-dashboard-paper) 95%, transparent)",
						}}
					>
						{MAT_LABEL[m.status]}
					</Pill>
					<div
						style={{ position: "absolute", left: 14, right: 14, bottom: 14 }}
					>
						<div
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 8.5,
								letterSpacing: "0.12em",
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 85%, transparent)",
								textTransform: "uppercase",
							}}
						>
							{m.room}
						</div>
						<div
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 19,
								fontWeight: 500,
								color: "var(--color-dashboard-on-fill)",
								marginTop: 3,
							}}
						>
							{m.n}
						</div>
						<div
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 11.5,
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 82%, transparent)",
								marginTop: 4,
								lineHeight: 1.4,
							}}
						>
							{m.spec}
						</div>
					</div>
				</div>

				{/* Right: quotes */}
				<div style={{ flex: 1, minWidth: 340, padding: "18px 22px" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 12,
						}}
					>
						<Eyebrow style={{ fontSize: 9 }}>
							{quotes.length} QUOTES · COMPARE
						</Eyebrow>
						<span
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 11.5,
								color: "var(--color-dashboard-muted)",
							}}
						>
							{m.qty}
						</span>
					</div>

					{/*
					 * Scales with the quote count instead of assuming three.
					 *
					 * The old form hardcoded exactly three slots and this grid was
					 * `repeat(3, 1fr)` to match. The API now accepts 1..n, so a
					 * single quote left two empty tracks and six squeezed into a
					 * cramped 3-wide, 2-row block. `auto-fit` with a minimum width
					 * does the right thing at both ends without a breakpoint.
					 */}
					<div
						style={{
							display: "grid",
							gridTemplateColumns:
								"repeat(auto-fit, minmax(min(160px, 100%), 1fr))",
							gap: 10,
						}}
					>
						{quotes.map((qq) => {
							const on = selId === qq.id;
							return (
								<button
									key={qq.id}
									type="button"
									onClick={() => !approved && setSelId(qq.id)}
									style={{
										textAlign: "left",
										cursor: approved ? "default" : "pointer",
										padding: 13,
										borderRadius: 8,
										border: 0,
										background: on
											? "var(--color-dashboard-paper)"
											: "var(--color-dashboard-surface)",
										boxShadow: on
											? `inset 0 0 0 2px ${accent}`
											: "inset 0 0 0 1px var(--color-dashboard-border)",
										transition: "all 160ms ease",
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
										}}
									>
										{/*
										 * Tone no longer keys off array position. It used to,
										 * which meant "whatever happens to be first" was styled
										 * as the recommended option -- a meaning nobody had
										 * assigned, and one that follows the array around when
										 * it is reordered.
										 */}
										<Pill
											tone={qq.vendorIsVerified ? "active" : "neutral"}
											dot={false}
											style={{ height: 17, fontSize: 8 }}
										>
											{qq.tag ?? (qq.vendorIsVerified ? "Verified" : "Quote")}
										</Pill>
										{on && <Icn name="checkCircle" size={16} color={accent} />}
									</div>
									<div
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12.5,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
											marginTop: 9,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{qq.vendor}
									</div>
									{/*
									 * The rating is the one `reviews` holds: people's ratings of
									 * this vendor after an approved BOQ line, labelled with what it
									 * measures. A vendor nobody has rated reads "Not yet rated",
									 * never 0.0. It replaces the per-quote star rating that was
									 * typed in before anything was bought.
									 */}
									<VendorRating
										rating={qq.vendorRating}
										className="mt-1 text-[11px]"
									/>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 4,
											marginTop: 3,
											flexWrap: "wrap",
										}}
									>
										<Icn
											name="info"
											size={11}
											color="var(--color-dashboard-muted)"
										/>
										<span
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 11,
												color: "var(--color-dashboard-muted)",
											}}
										>
											{qq.lead}
											{qq.vendorCity ? ` · ${qq.vendorCity}` : ""}
										</span>
									</div>
									{qq.addedByRole && (
										<div
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 10.5,
												color: "var(--color-dashboard-muted)",
												marginTop: 3,
											}}
										>
											added by your {qq.addedByRole}
										</div>
									)}
									<div
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 500,
											fontSize: 20,
											color: "var(--color-dashboard-text)",
											marginTop: 8,
											letterSpacing: "-0.015em",
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{fmt(qq.price)}/{qq.unit}
									</div>
								</button>
							);
						})}
					</div>

					{/* Bottom action row */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							marginTop: 16,
							paddingTop: 14,
							borderTop: "1px solid var(--color-dashboard-border-soft)",
							flexWrap: "wrap",
							gap: 10,
						}}
					>
						{approved ? (
							<>
								<span
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: 6,
										fontFamily: "var(--font-body)",
										fontSize: 13,
										fontWeight: 600,
										color: "var(--color-dashboard-pill-done)",
									}}
								>
									<Icn
										name="checkCircle"
										size={16}
										color="var(--color-dashboard-pill-done)"
									/>
									{/*
									 * Every field here comes from the APPROVED quote.
									 *
									 * This line used to read `{m.vendor} · {fmt(m.cost)}` with
									 * only the unit taken from the approved quote -- and neither
									 * of those is the approved quote: `m.vendor` is the product's
									 * BRAND and `m.cost` is the CHEAPEST quote's price. Approving
									 * the dearer of two quotes produced "Approved · Kajaria ·
									 * Rs400/sq_ft" for a Rs600 Ravi Traders quote: wrong vendor,
									 * wrong price, right unit, on the one line that confirms what
									 * the homeowner just committed to.
									 */}
									Approved · {approvedQuote?.vendor} ·{" "}
									{approvedQuote ? fmt(approvedQuote.price) : "—"}/
									{approvedQuote?.unit}
								</span>
								<StudioButton
									variant="secondary"
									size="sm"
									onClick={() => setChanging(true)}
									style={secondaryBtnStyle("sm")}
								>
									Change choice
								</StudioButton>
							</>
						) : (
							<>
								<span
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 12.5,
										color: "var(--color-dashboard-muted)",
									}}
								>
									Selected:{" "}
									<strong style={{ color: "var(--color-dashboard-text)" }}>
										{selected?.vendor ?? "None"}
									</strong>
									{selected ? (
										<>
											{" "}
											· {fmt(selected.price)}/{selected.unit}
											{/*
											 * Compared against the CHEAPEST quote, not against
											 * whatever sits at index 0. The old comparison used
											 * `quotes[0]` as the baseline, so the saving it
											 * reported changed when the array was reordered even
											 * though no price had moved.
											 */}
											{cheapest && selected.id !== cheapest.id && (
												<span
													style={{
														color: "var(--color-dashboard-pill-awaiting)",
													}}
												>
													{" "}
													(adds {fmt(selected.price - cheapest.price)} over the
													cheapest)
												</span>
											)}
										</>
									) : null}
								</span>
								<Btn
									variant="primary"
									size="sm"
									icon={
										isApproving ? (
											<Icn
												name="refreshCw"
												size={16}
												color="var(--color-dashboard-on-fill)"
											/>
										) : (
											<Icn
												name="check"
												size={16}
												color="var(--color-dashboard-on-fill)"
											/>
										)
									}
									disabled={!selected || isApproving}
									onClick={() =>
										selected &&
										onApprove(
											m.n,
											{
												name: m.n,
												price: selected.price,
												vendor: selected.vendor,
											},
											selected.id,
										)
									}
								>
									{isApproving ? "Approving..." : "Approve this quote"}
								</Btn>
								{changing && !isApproving && (
									<StudioButton
										variant="secondary"
										size="sm"
										onClick={() => setChanging(false)}
										style={secondaryBtnStyle("sm")}
									>
										Cancel
									</StudioButton>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</FloatPanel>
	);
}

interface HoSelectionsTabProps {
	projectId: string;
	accent?: string;
}

function transformShortlistToMaterials(
	shortlist: Awaited<ReturnType<typeof useProjectShortlist>["data"]>,
	approvedQuotes: ReadonlyMap<string, string>,
): MaterialItem[] {
	if (!shortlist) return [];

	return shortlist.map((entry) => {
		const productName = entry.product?.name || "Unknown Product";
		const vendorName = entry.product?.brand?.name || "";
		const imageUrl =
			entry.product?.images?.[0] || `${ASSETS_URL}placeholder.webp`;
		/*
		 * The SERVER's record of the approval, with a local overlay covering the
		 * moment between clicking and the refetch landing.
		 *
		 * The overlay carries the QUOTE ID, not just the fact of approval. Holding
		 * only a set of entry ids flipped the card to "Approved" while
		 * `selectedQuoteId` was still null, so the approved line fell back to the
		 * product's brand and the cheapest quote -- printing the wrong vendor at the
		 * wrong price for about a second, which is precisely the defect the line was
		 * rewritten to remove. The overlay is read first, because after "Change
		 * choice" the server's value is the previous quote until the refetch
		 * lands; and it is dropped once the server's value matches it (below), so
		 * a later change made elsewhere is not hidden behind it.
		 */
		const optimisticQuoteId = approvedQuotes.get(entry.id) ?? null;
		const selectedQuoteId = optimisticQuoteId ?? entry.selectedQuoteId;
		const approved = !!selectedQuoteId;

		// The cheapest quote, as a reference price. Was `quotes[0]`, which is a
		// position rather than a fact: reordering the list changed the number this
		// screen called the product's cost without any price having moved.
		const referencePrice =
			entry.quotes && entry.quotes.length > 0
				? Math.min(...entry.quotes.map((q) => q.price))
				: 0;

		return {
			id: entry.id,
			n: productName,
			room: "General",
			vendor: vendorName,
			cost: referencePrice,
			spec: entry.variant?.variantAttributes
				? Object.values(entry.variant.variantAttributes).join(", ")
				: entry.notes || "No specifications",
			img: imageUrl,
			status: approved ? "approved" : "pending",
			qty: `${entry.quantity} unit${entry.quantity !== 1 ? "s" : ""}`,
			shortlistEntryId: entry.id,
			quotes: entry.quotes,
			selectedQuoteId,
		};
	});
}

export function HoSelectionsTab({
	projectId,
	accent = "var(--color-dashboard-accent)",
}: HoSelectionsTabProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { data: shortlist, isLoading, error } = useProjectShortlist(projectId);
	// The designer's name is no longer needed here. The banner used to say
	// "{designer} has gathered three verified quotes"; contractors can add them
	// too now, and the count varies, so attributing the whole set to one person
	// was wrong on both halves.
	// entry id -> the quote id the homeowner just approved, until the server's
	// `selectedQuoteId` carries it.
	const [approvedQuotes, setApprovedQuotes] = React.useState<
		ReadonlyMap<string, string>
	>(new Map());
	React.useEffect(() => {
		if (!shortlist || approvedQuotes.size === 0) return;
		const confirmed = shortlist.filter(
			(entry) =>
				approvedQuotes.has(entry.id) &&
				approvedQuotes.get(entry.id) === entry.selectedQuoteId,
		);
		if (confirmed.length === 0) return;
		setApprovedQuotes((prev) => {
			const next = new Map(prev);
			for (const entry of confirmed) next.delete(entry.id);
			return next;
		});
	}, [shortlist, approvedQuotes]);
	// Entries with an approval on its way, one per card, so one finishing does
	// not end another card's.
	const [approving, setApproving] = React.useState<ReadonlySet<string>>(
		new Set(),
	);

	const allMaterials = React.useMemo(
		() => transformShortlistToMaterials(shortlist, approvedQuotes),
		[shortlist, approvedQuotes],
	);

	// Quote-less entries are RENDERED, not filtered out.
	//
	// Filtering them meant a designer who shortlisted a product but had not yet
	// gathered quotes had shortlisted it into a void: the homeowner never saw it,
	// and nothing anywhere said why. The card already has a "no quotes added yet"
	// state; only this filter stopped it from ever being reached.
	const materials = allMaterials;

	// Only entries that CAN be approved count towards the progress denominator.
	// Including quote-less ones would make the number unreachable by definition --
	// the homeowner has nothing to act on for those.
	const awaitingDecision = materials.filter(
		(m) => m.quotes && m.quotes.length > 0,
	);
	const approvedCount = awaitingDecision.filter(
		(m) => m.status === "approved",
	).length;
	const quotelessCount = materials.length - awaitingDecision.length;

	const [approvalNote, setApprovalNote] = React.useState<string | null>(null);

	const handleApprove = async (entryId: string, quoteId: string) => {
		if (!projectId) return;
		setApproving((prev) => new Set(prev).add(entryId));
		setApprovalNote(null);
		try {
			// The dedicated approval endpoint, not a general-purpose PATCH of the
			// entry. It is the only path authorized as the homeowner's action, and
			// the only one that writes the price through to the BOQ.
			const result = await selectQuote(projectId, entryId, quoteId);

			// Say what actually happened. The defect this replaces was an approval
			// that silently did nothing -- so "approved" on its own is exactly the
			// message that was wrong before.
			setApprovalNote(
				result.boqSkipped
					? result.boqSkipped.message
					: result.repricedEntryIds.length > 0
						? `Approved. ${result.repricedEntryIds.length} BOQ line${
								result.repricedEntryIds.length === 1 ? "" : "s"
							} updated to this price, and now need your re-approval.`
						: "Approved. No BOQ line changed.",
			);

			setApprovedQuotes((prev) => new Map(prev).set(entryId, quoteId));
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects.shortlist(projectId),
			});
		} catch (err) {
			const message =
				(err as { response?: { data?: { message?: string } } })?.response?.data
					?.message ?? "Could not approve this quote.";
			setApprovalNote(message);
		} finally {
			setApproving((prev) => {
				const next = new Set(prev);
				next.delete(entryId);
				return next;
			});
		}
	};

	if (isLoading) {
		return (
			<SkeletonRows
				label={t("loaders.loadingSelections")}
				className="min-h-75"
			/>
		);
	}

	if (error) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 300,
				}}
			>
				<Eyebrow style={{ color: "var(--color-dashboard-pill-risk)" }}>
					Failed to load selections
				</Eyebrow>
			</div>
		);
	}

	if (!shortlist || materials.length === 0) {
		return (
			<div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-end",
						marginBottom: 20,
					}}
				>
					<div>
						<Eyebrow>SELECTIONS · 0 SIGNED OFF</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 30,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 0",
							}}
						>
							Compare quotes,{" "}
							<ItalicAccent size={30} color={accent}>
								then choose
							</ItalicAccent>
						</h2>
					</div>
				</div>

				<FloatPanel elev={1} padding={32}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 16,
							textAlign: "center",
						}}
					>
						<Icn name="grid" size={40} color="var(--color-dashboard-muted)" />
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								maxWidth: 400,
							}}
						>
							No vendor quotes added yet. The designer will add quotes for your
							materials soon.
						</p>
					</div>
				</FloatPanel>
			</div>
		);
	}

	return (
		<div>
			{/* Header */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					marginBottom: 20,
				}}
			>
				<div>
					<Eyebrow>
						SELECTIONS · {approvedCount}/{awaitingDecision.length} SIGNED OFF
						{quotelessCount > 0 ? ` · ${quotelessCount} AWAITING QUOTES` : ""}
					</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 30,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						Compare quotes,{" "}
						<ItalicAccent size={30} color={accent}>
							then choose
						</ItalicAccent>
					</h2>
				</div>
			</div>

			{/* Info banner */}
			<div
				style={{
					display: "flex",
					gap: 10,
					padding: "13px 18px",
					background: "var(--color-dashboard-surface)",
					borderRadius: 8,
					marginBottom: 22,
					alignItems: "flex-start",
				}}
			>
				<Icn
					name="info"
					size={15}
					color={accent}
					style={{ marginTop: 1, flexShrink: 0 }}
				/>
				<span
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						lineHeight: 1.5,
						color: "var(--color-dashboard-muted-dark)",
					}}
				>
					{/*
					 * `writeQuoteToBoq` prices a line only when the product is already
					 * on the project's latest BOQ and that BOQ is a draft or has
					 * changes requested. Nothing applies the choice to a line added
					 * later, so this does not promise that.
					 */}
					Compare the quotes on each item and choose one. If the item is on a
					BOQ your designer is still editing, your choice updates that line's
					price, and the line comes back to you for approval. Otherwise the
					BOQ's prices stay as they are, and the note after you choose says why.
				</span>
			</div>

			{/*
			 * What the approval actually did.
			 *
			 * The defect this screen exists to fix was an approval that silently did
			 * nothing -- the copy promised the choice "locks the price into your BOQ"
			 * and no BOQ line ever changed. So the result is reported: which lines
			 * moved, that they need re-approval at the new price, or why the write
			 * could not happen. "Approved" on its own is the message that was wrong.
			 */}
			{approvalNote && (
				<div
					style={{
						display: "flex",
						gap: 10,
						padding: "12px 16px",
						background: "var(--color-dashboard-success-bg)",
						borderRadius: 8,
						marginBottom: 16,
						alignItems: "flex-start",
					}}
				>
					<Icn
						name="checkCircle"
						size={15}
						color="var(--color-dashboard-success)"
						style={{ marginTop: 1, flexShrink: 0 }}
					/>
					<span
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 12.5,
							lineHeight: 1.5,
							color: "var(--color-dashboard-text)",
						}}
					>
						{approvalNote}
					</span>
				</div>
			)}

			{/* Material cards */}
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				{materials.map((m) => (
					/*
					 * Keyed by entry id so a test can address ONE card. Every card
					 * carries the same "Approve this quote" button, so without this the
					 * only way to reach a particular entry's button is by position in a
					 * list whose contents and order are seed data.
					 */
					<div
						key={m.id || m.n}
						data-testid={`selection-entry-${m.shortlistEntryId ?? m.id}`}
					>
						<SelectionCompareCard
							m={m}
							accent={accent}
							onApprove={(_name, _choice, quoteId) => {
								if (m.shortlistEntryId) {
									handleApprove(m.shortlistEntryId, quoteId);
								}
							}}
							isApproving={
								!!m.shortlistEntryId && approving.has(m.shortlistEntryId)
							}
						/>
					</div>
				))}
			</div>
		</div>
	);
}
